import { Localized, useLocalization } from "@fluent/react";
import { Button, useBreakpoints } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { addDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { SimpleSlider } from "@lending/components/SimpleSlider/SimpleSlider";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useCheckVehicleLoanMembershipRequirementQuery } from "@lending/hooks/queries/AutoLoanApplication/useCheckVehicleLoanMembershipRequirementQuery";
import { useDefaultCarImageQuery } from "@lending/hooks/queries/useDefaultCarImageQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { useListVehicleLoanOffersQuery } from "@lending/hooks/queries/VehicleLoanOffer/useListVehicleLoanOffersQuery";
import { useCreateVehicleLoanOfferSelectionQuery } from "@lending/hooks/queries/VehicleLoanOfferSelection/useCreateVehicleLoanOfferSelectionQuery";
import { useApiErrorToast } from "@lending/hooks/useApiErrorToast";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useFlowNavigation } from "@lending/navigation";
import { navigateToMembershipShareStartStep } from "@lending/pages/Common/MembershipShare/hooks/navigateToMembershipShareStartStep";
import { calculateMonthlyPayment } from "@lending/pages/PersonalLoan/Offers/utils/loanCalculations";
import { APPLICATION_RELATIONSHIP, type VehicleLoanOffer } from "@lending/types";
import {
  capitalizeFirstLetter,
  formatCentsAsDollars,
  formatCentsAsDollarsAndCents,
  formatShortDate,
} from "@lending/utils/formatters";
import styles from "./Approved.module.css";

const MIN_AMOUNT_CENTS_FALLBACK = 100000;
const AMOUNT_STEP_CENTS_FALLBACK = 100000;

const formatAprPercent = (aprBasis: number) => (aprBasis / 100).toFixed(2);

type VehicleLoanApplicationData = NonNullable<
  ReturnType<typeof useGetVehicleLoanApplicationQuery>["data"]
>;

interface ApprovedContentProps {
  applicantFirstName: string;
  application: VehicleLoanApplicationData;
  /** Institution-configured days an offer stays valid, counted from `offer.created_at`. */
  expirationDays: number;
  imageUrl?: string;
  fallbackImageUrl?: string;
  infoLinkUrl?: string;
  institutionName: string;
  isPending: boolean;
  l10n: ReturnType<typeof useLocalization>["l10n"];
  m: boolean;
  maxAmountCents: number;
  maxTermMonths: number;
  minAmountCents: number;
  minTermMonths: number;
  amountStepCents: number;
  offers: VehicleLoanOffer[];
  onAcceptOffer: (args: {
    productOfferId: string;
    selectedAmountCents: number;
    selectedTermMonths: number;
  }) => Promise<void>;
}

function AmountAndMonthsSliders({
  amountCents,
  setAmountCents,
  monthsValue,
  setMonthsValue,
  minAmountCents,
  maxAmountCents,
  amountStepCents,
  minTermMonths,
  maxTermMonths,
  l10n,
}: {
  amountCents: number;
  setAmountCents: (value: number) => void;
  monthsValue: number;
  setMonthsValue: (value: number) => void;
  minAmountCents: number;
  maxAmountCents: number;
  amountStepCents: number;
  minTermMonths: number;
  maxTermMonths: number;
  l10n: ReturnType<typeof useLocalization>["l10n"];
}) {
  return (
    <div className={styles.slidersCard}>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderLabel}>
          {l10n.getString("auto-loan-approved-want-to-borrow", null, "I want to borrow")}
          <span className={styles.sliderLabelNumber}>{formatCentsAsDollars(amountCents)}</span>
        </div>
        <SimpleSlider
          min={minAmountCents}
          max={maxAmountCents}
          step={amountStepCents}
          value={amountCents}
          setValue={setAmountCents}
        />
      </div>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderLabel}>
          {l10n.getString("auto-loan-approved-pay-back-over", null, "and pay back over")}
          <span className={styles.sliderLabelNumber}>
            {monthsValue} {l10n.getString("months", null, "months")}
          </span>
        </div>
        <SimpleSlider
          min={minTermMonths}
          max={maxTermMonths}
          step={1}
          value={monthsValue}
          setValue={setMonthsValue}
        />
      </div>
    </div>
  );
}

function ApprovedContent({
  applicantFirstName,
  application,
  expirationDays,
  imageUrl,
  fallbackImageUrl,
  infoLinkUrl,
  institutionName,
  isPending,
  l10n,
  m,
  maxAmountCents,
  maxTermMonths,
  minAmountCents,
  minTermMonths,
  amountStepCents,
  offers,
  onAcceptOffer,
}: ApprovedContentProps) {
  const [amountCents, setAmountCents] = useState(maxAmountCents);
  const [monthsValue, setMonthsValue] = useState(maxTermMonths);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [displayedImageUrl, setDisplayedImageUrl] = useState<string | undefined>(
    imageUrl ?? fallbackImageUrl
  );

  const currentOffer = useMemo(
    () => offers.find((offer) => offer.max_term_months >= monthsValue) ?? offers[offers.length - 1],
    [offers, monthsValue]
  );

  const aprPercent = currentOffer.apr_basis / 100;
  const aprDisplay = formatAprPercent(currentOffer.apr_basis);
  const expirationDate = formatShortDate(
    addDays(new Date(currentOffer.created_at), expirationDays)
  );
  const monthlyPaymentValue = useMemo(
    () => calculateMonthlyPayment(amountCents, aprPercent, monthsValue),
    [amountCents, aprPercent, monthsValue]
  );

  const hasCompleteVehicleSelection = Boolean(
    application.vehicle_make && application.vehicle_model
  );
  const formattedMake = capitalizeFirstLetter(application.vehicle_make ?? "");
  const formattedModel = capitalizeFirstLetter(application.vehicle_model ?? "");
  const fullVehicleName = hasCompleteVehicleSelection ? `${formattedMake} ${formattedModel}` : "";
  const ctaLabel = l10n.getString("button-label-get-started", null, "Get started");

  useEffect(() => {
    setImageLoaded(false);
    setDisplayedImageUrl(imageUrl ?? fallbackImageUrl);
  }, [imageUrl, fallbackImageUrl]);

  const handleImageError = () => {
    // If the primary (Evox) URL failed to load in the browser, drop to the
    // institution-configured fallback. If we're already on the fallback and it
    // also failed, hide the image entirely.
    setDisplayedImageUrl((current) => {
      if (current && current !== fallbackImageUrl && fallbackImageUrl) {
        return fallbackImageUrl;
      }
      return undefined;
    });
    setImageLoaded(false);
  };

  return (
    <PageLayout>
      <div className={styles.container}>
        <div className={styles.contentMain}>
          {m && (
            <div className={styles.verticalContent}>
              <div className={styles.titleSection}>
                <div className="fontSize--l fontWeight--bold">
                  {l10n.getString(
                    "auto-loan-approved-good-news",
                    { firstName: applicantFirstName },
                    `Good news, ${applicantFirstName}!`
                  )}
                </div>
                <h1 className={styles.heading}>
                  {l10n.getString("auto-loan-prequalified", null, "You're pre-qualified up to")}{" "}
                  <span className={styles.headingDollarAmount}>
                    {formatCentsAsDollars(maxAmountCents)}
                  </span>
                </h1>
              </div>
              <AmountAndMonthsSliders
                amountCents={amountCents}
                setAmountCents={setAmountCents}
                monthsValue={monthsValue}
                setMonthsValue={setMonthsValue}
                minAmountCents={minAmountCents}
                maxAmountCents={maxAmountCents}
                amountStepCents={amountStepCents}
                minTermMonths={minTermMonths}
                maxTermMonths={maxTermMonths}
                l10n={l10n}
              />
            </div>
          )}
          <div className={styles.verticalContent}>
            {!m && (
              <div className={styles.mobileTitleSection}>
                <div className="fontSize--l fontWeight--bold">
                  {l10n.getString(
                    "auto-loan-approved-good-news",
                    { firstName: applicantFirstName },
                    `Good news, ${applicantFirstName}!`
                  )}
                </div>
                <h1 className={styles.mobileHeading}>
                  {l10n.getString("auto-loan-prequalified", null, "You're pre-qualified up to")}{" "}
                  <span className={styles.mobileHeadingDollarAmount}>
                    {formatCentsAsDollars(maxAmountCents)}
                  </span>
                </h1>
              </div>
            )}
            <div className={styles.detailsCard}>
              {displayedImageUrl && (
                <img
                  className={`${styles.carImage} ${imageLoaded ? styles.carImageLoaded : ""}`}
                  src={displayedImageUrl}
                  alt={fullVehicleName}
                  onLoad={() => setImageLoaded(true)}
                  onError={handleImageError}
                />
              )}
              <div className={styles.detailsContent} style={{ paddingTop: imageLoaded ? 80 : 32 }}>
                <div className={styles.monthlyPayment}>
                  {formatCentsAsDollarsAndCents(monthlyPaymentValue)}{" "}
                  <span className={styles.monthlyPaymentSubtext}>
                    / {l10n.getString("month", null, "month")}
                  </span>
                </div>
                <div className={styles.vehicleName}>{fullVehicleName}</div>
                {!m && (
                  <AmountAndMonthsSliders
                    amountCents={amountCents}
                    setAmountCents={setAmountCents}
                    monthsValue={monthsValue}
                    setMonthsValue={setMonthsValue}
                    minAmountCents={minAmountCents}
                    maxAmountCents={maxAmountCents}
                    amountStepCents={amountStepCents}
                    minTermMonths={minTermMonths}
                    maxTermMonths={maxTermMonths}
                    l10n={l10n}
                  />
                )}
                <div className={styles.offerDetailsCard}>
                  <div className={styles.offerDetailsRow}>
                    <div className="narmi-icon-check" />
                    {l10n.getString(
                      "auto-loan-approved-apr",
                      { apr: aprDisplay },
                      `${aprDisplay}% APR`
                    )}
                  </div>
                  <div className={styles.offerDetailsRow}>
                    <div className="narmi-icon-check" />
                    {l10n.getString(
                      "auto-loan-approved-expiration-label",
                      { date: expirationDate },
                      `Offer expires ${expirationDate}`
                    )}
                  </div>
                  <div className={styles.offerDetailsRow}>
                    <div className="narmi-icon-check" />
                    {l10n.getString("auto-loan-approved-bullet-2", null, "Gap insurance available")}
                  </div>
                  {infoLinkUrl && (
                    <Button
                      as="a"
                      kind="plain"
                      label={l10n.getString("ui-info-disclaimer", null, "Info & disclaimer")}
                      ariaLabel={l10n.getString(
                        "ui-info-disclaimer-aria-label",
                        null,
                        "Info & disclaimer (opens in a new tab)"
                      )}
                      size="xs"
                      href={infoLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )}
                </div>
                <Button
                  variant="primary"
                  size="m"
                  endIcon="arrow-right"
                  isFullWidth
                  disabled={isPending}
                  onClick={() => {
                    void onAcceptOffer({
                      productOfferId: currentOffer.id,
                      selectedAmountCents: amountCents,
                      selectedTermMonths: monthsValue,
                    });
                  }}
                >
                  {ctaLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <div className="fontSize--s fontWeight--thin">
            <Localized id="offers-consent-disclaimer-auto" vars={{ ctaLabel, institutionName }}>
              {`By clicking "${ctaLabel}" above, you consent to authorize ${institutionName} to obtain and use Consumer Reports about you to review your application, to review the products or services you maintain, and to analyze your financial needs and to market other products and services to you.`}
            </Localized>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export function Approved() {
  const { l10n } = useLocalization();
  const { goToPath, goToStep } = useFlowNavigation();
  const { m } = useBreakpoints();
  const { institution } = useGetInstitutionQuery();
  const institutionName = institution.short_name ?? "";
  const handleApiError = useApiErrorToast();

  const lendingApplicationID = useLendingApplicationID();
  const {
    data: application,
    isLoading: isApplicationLoading,
    refetch: refetchApplication,
  } = useGetVehicleLoanApplicationQuery();
  const { data: applicantResponse } = useListApplicationApplicantDetailsQuery();
  const applicant = applicantResponse?.results?.find(
    (a) => a.application_relationship === APPLICATION_RELATIONSHIP.PRIMARY
  );

  // Alloy returns one offer per term tier. The backend converts those into contiguous
  // bracket windows so any term the user picks falls into exactly one offer, whose APR
  // is the rate for that bracket.
  const {
    data: offersData,
    isLoading: isOffersLoading,
    isError: isOffersError,
  } = useListVehicleLoanOffersQuery();
  const offers = useMemo<VehicleLoanOffer[]>(
    () => [...(offersData?.results ?? [])].sort((a, b) => a.max_term_months - b.max_term_months),
    [offersData]
  );

  const hasOffers = offers.length > 0;
  const minTermMonths = hasOffers ? offers[0].min_term_months : 0;
  const maxTermMonths = hasOffers ? offers[offers.length - 1].max_term_months : 0;
  const maxAmountCents = hasOffers
    ? Math.max(...offers.map((offer) => offer.offered_amount_cents))
    : 0;

  const amountParams = institution.lending_vehicle_loan_requested_amount_params as
    | { min?: number; step?: number }
    | undefined;
  const configuredMinAmountCents = amountParams?.min ?? MIN_AMOUNT_CENTS_FALLBACK;
  const amountStepCents = amountParams?.step ?? AMOUNT_STEP_CENTS_FALLBACK;
  const minAmountCents = Math.min(configuredMinAmountCents, maxAmountCents);

  const navigateToSubmitted = () => {
    goToStep("submitted");
  };

  const routeFromRecoveredApplicationState = async (state?: string) => {
    if (!state) {
      handleApiError(undefined);
      return;
    }

    if (state === "offer_accepted") {
      return;
    }

    if (state === "membership_share_pending" || state === "membership_selection") {
      await navigateToMembershipShareStartStep({
        applicationType: "auto-loan",
        lendingApplicationID,
        state,
        verificationRequired: false,
        goToPath,
      });
      return;
    }

    navigateToSubmitted();
  };

  const recoverFromMembershipCheckError = async () => {
    try {
      const { data: refreshedApplication } = await refetchApplication();

      await routeFromRecoveredApplicationState(refreshedApplication?.state);
    } catch {
      handleApiError(undefined);
    }
  };

  const { mutateAsync: checkMembershipRequirement, isPending: isCheckingMembership } =
    useCheckVehicleLoanMembershipRequirementQuery({
      onSuccess: async ({
        state,
        membership_share_verification_required: verificationRequired,
        membership_share_email_conflict: emailConflict,
      }) => {
        if (state === "membership_share_pending" || state === "membership_selection") {
          await navigateToMembershipShareStartStep({
            applicationType: "auto-loan",
            lendingApplicationID,
            state,
            verificationRequired,
            emailConflict,
            goToPath,
          });
        } else {
          navigateToSubmitted();
        }
      },
      onError: () => {
        void recoverFromMembershipCheckError();
      },
    });

  const { mutateAsync: createOfferSelection, isPending: isCreatingSelection } =
    useCreateVehicleLoanOfferSelectionQuery({
      onSuccess: async () => {
        if (application) {
          try {
            // @ts-expect-error schema incorrectly requires body for this action
            await checkMembershipRequirement({
              params: { path: { uuid: application.id } },
            });
          } catch {
            return;
          }
        }
      },
      onError: handleApiError,
    });

  const infoLinkUrl =
    institution.lending_vehicle_loan_offer_info_and_disclaimer_url ||
    institution.lending_offer_info_and_disclaimer_url;
  const fallbackImageUrl = institution.lending_vehicle_loan_default_car_image_url || undefined;
  const hasVehicleMake = Boolean(application?.vehicle_make);
  const hasVehicleModel = Boolean(application?.vehicle_model);
  const hasCompleteVehicleSelection = hasVehicleMake && hasVehicleModel;
  const hasPartialVehicleSelection = hasVehicleMake !== hasVehicleModel;

  useEffect(() => {
    if (application?.id && hasPartialVehicleSelection) {
      Sentry.captureMessage(
        "A user navigated to the auto loan approved screen with only a partial vehicle selection. Falling back to the institution default car image."
      );
    }
  }, [application?.id, hasPartialVehicleSelection]);

  const defaultCarImageQuery = useDefaultCarImageQuery({
    year: application?.vehicle_year ?? null,
    make: application?.vehicle_make ?? "",
    model: application?.vehicle_model ?? "",
    trim: application?.vehicle_trim ?? "",
    enabled: hasCompleteVehicleSelection,
  });

  const imageUrl = defaultCarImageQuery.data?.url ?? undefined;

  useEffect(() => {
    if (
      application?.id &&
      hasCompleteVehicleSelection &&
      !defaultCarImageQuery.isLoading &&
      defaultCarImageQuery.data !== undefined &&
      !imageUrl
    ) {
      Sentry.captureMessage(
        `Evox returned no image for vehicle ${application.vehicle_make ?? "?"} ${application.vehicle_model ?? "?"} ${application.vehicle_trim ?? ""} ${application.vehicle_year ?? ""}. Falling back to the institution default car image.`
      );
    }
  }, [
    application?.id,
    application?.vehicle_make,
    application?.vehicle_model,
    application?.vehicle_trim,
    application?.vehicle_year,
    hasCompleteVehicleSelection,
    defaultCarImageQuery.isLoading,
    defaultCarImageQuery.data,
    imageUrl,
  ]);

  // If we don't have an application ID, application data, or applicant details,
  // something went wrong and we should navigate back to product selection.
  useEffect(() => {
    if (!lendingApplicationID || (!isApplicationLoading && (!application || !applicant))) {
      goToPath("/product-selection", { replace: true });
    }
  }, [lendingApplicationID, application, applicant, isApplicationLoading, goToPath]);

  if (isApplicationLoading || isOffersLoading || defaultCarImageQuery.isLoading) {
    return <PageLayout isLoading>{null}</PageLayout>;
  }

  if (isOffersError || !hasOffers || !application || !applicant) {
    Sentry.captureMessage(
      "A user navigated to the auto loan approved screen but did not have an offer. This should not happen."
    );
    return <ErrorScreen />;
  }

  const isPending = isCreatingSelection || isCheckingMembership;

  async function acceptOfferAndCheckMembership({
    productOfferId,
    selectedAmountCents,
    selectedTermMonths,
  }: {
    productOfferId: string;
    selectedAmountCents: number;
    selectedTermMonths: number;
  }) {
    try {
      if (application?.state === "offer_accepted") {
        // @ts-expect-error schema incorrectly requires body for this action
        await checkMembershipRequirement({
          params: { path: { uuid: lendingApplicationID } },
        });
        return;
      }

      await createOfferSelection({
        // @ts-expect-error openapi-typescript does not respect readonly payload fields
        body: {
          application: lendingApplicationID,
          product_offer: productOfferId,
          selected_amount_cents: selectedAmountCents,
          selected_term_months: selectedTermMonths,
        },
      });
    } catch {
      // onError surfaces the failure in a toast; swallow the rethrow so the
      // promise from the click handler does not become an unhandled rejection.
    }
  }

  return (
    <ApprovedContent
      applicantFirstName={applicant.first_name ?? ""}
      application={application}
      expirationDays={institution.lending_application_offers_expiration_days}
      imageUrl={imageUrl}
      fallbackImageUrl={fallbackImageUrl}
      infoLinkUrl={infoLinkUrl}
      institutionName={institutionName}
      isPending={isPending}
      l10n={l10n}
      m={m}
      maxAmountCents={maxAmountCents}
      maxTermMonths={maxTermMonths}
      minAmountCents={minAmountCents}
      minTermMonths={minTermMonths}
      amountStepCents={amountStepCents}
      offers={offers}
      onAcceptOffer={acceptOfferAndCheckMembership}
    />
  );
}
